import { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import useEmblaCarousel from 'embla-carousel-react'

import { media } from '../../styles/media'

import ArrowLeft from '../icons/ArrowLeft'
import ArrowRight from '../icons/ArrowRight'
import CrossOutIcon from '../icons/CrossOut'
import VideoIcon from '../icons/Video'

import Modal from './Modal'
import StreamableVideo from './StreamableVideo'

const MainSlide = styled.div`
  height: 100%;
  width: 100%;

  background-color: black;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  ${({ left }) => (left ? 'left: 0;' : 'right: 0;')}
  transform: translateY(-50%);

  padding: 20px;
  border: 0;
  color: white;
  background-color: rgba(0, 0, 0, 0.6);

  svg {
    display: block;
    width: 16px;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  padding: 11px;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: white;

  svg {
    display: block;
    width: 24px;
    height: 24px;
  }

  @media (min-width: 768px) {
    top: 20px;
    right: 20px;
  }
`

const FullscreenContainer = styled.div`
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`

const FullscreenWrapper = styled.div`
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  flex-direction: column;
`

const MainViewport = styled.div`
  overflow: hidden;
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
`

const MainContainer = styled.div`
  display: flex;
  height: 100%;
`

const MainSlideWrapper = styled.div`
  position: relative;
  flex: 0 0 100%;
  min-width: 0;
  height: 100%;
  background: black;
`

const ThumbsViewport = styled.div`
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.6);
`

const ThumbsContainer = styled.div`
  display: flex;
  gap: 2px;
`

const ThumbSlide = styled.button.attrs({ type: 'button' })`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 120px;
  height: 80px;
  min-width: 0;
  border: 2px solid ${({ $selected }) => ($selected ? 'white' : 'transparent')};
  padding: 0;
  cursor: pointer;
  background-color: black;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  opacity: ${({ $selected }) => ($selected ? 1 : 0.5)};
  transition:
    opacity 200ms ease,
    border-color 200ms ease;

  ${({ $backgroundImage }) =>
    $backgroundImage &&
    `
    background-image: url(${$backgroundImage});
  `}

  svg {
    position: absolute;
    left: 50%;
    top: 50%;
    display: block;
    color: white;
    width: 32px;
    transform: translate(-50%, -50%);
  }

  ${media.md`
    flex: 0 0 150px;
    height: 100px;
  `}
`

const FullscreenSlider = ({ slides, onRequestClose, isOpen }) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [mainRef, mainApi] = useEmblaCarousel({ loop: true })
  const [thumbsRef, thumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  })

  const onSelect = useCallback(() => {
    if (!mainApi || !thumbsApi) return
    setSelectedIndex(mainApi.selectedScrollSnap())
    thumbsApi.scrollTo(mainApi.selectedScrollSnap())
  }, [mainApi, thumbsApi])

  useEffect(() => {
    if (!mainApi) return undefined

    onSelect()
    mainApi.on('select', onSelect)
    mainApi.on('reInit', onSelect)

    return () => {
      mainApi.off('select', onSelect)
      mainApi.off('reInit', onSelect)
    }
  }, [mainApi, onSelect])

  const handleThumbClick = useCallback(
    (index) => {
      if (!mainApi) return
      mainApi.scrollTo(index)
    },
    [mainApi],
  )

  const handlePrev = useCallback(() => {
    mainApi?.scrollPrev()
  }, [mainApi])

  const handleNext = useCallback(() => {
    mainApi?.scrollNext()
  }, [mainApi])

  // Focus the main slider and add keyboard navigation when modal opens
  useEffect(() => {
    if (!mainApi || !isOpen) return undefined

    const viewport = mainApi.rootNode()
    const timer = setTimeout(() => {
      if (viewport) {
        viewport.setAttribute('tabindex', '0')
        viewport.focus()
      }
    }, 0)

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        handlePrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        handleNext()
      }
    }

    if (viewport) viewport.addEventListener('keydown', handleKeyDown)

    return () => {
      clearTimeout(timer)
      if (viewport) viewport.removeEventListener('keydown', handleKeyDown)
    }
  }, [mainApi, isOpen, handlePrev, handleNext])

  const allSlides = []
  const allThumbs = []

  slides.forEach((slide) => {
    if (slide.type === 'video') {
      const slideIndex = allSlides.length
      allSlides.push(
        <MainSlideWrapper key={`video-${slide.id}`}>
          <MainSlide>
            <StreamableVideo
              src={slide?.streamUrl}
              poster={slide?.posterUrl}
              objectFit="contain"
              forcePause={selectedIndex !== slideIndex}
              browserControls
            />
          </MainSlide>
        </MainSlideWrapper>,
      )
      allThumbs.push({
        key: `video-${slide.id}`,
        isVideo: true,
        backgroundImage: slide?.thumbnailUrl,
      })
    } else {
      const photoKey = `photo-${slide.position}`
      allSlides.push(
        <MainSlideWrapper key={photoKey}>
          <MainSlide>
            <img
              className="lazyload"
              data-src={slide.image.large}
              alt="slide"
            />
          </MainSlide>
        </MainSlideWrapper>,
      )
      allThumbs.push({
        key: photoKey,
        isVideo: false,
        backgroundImage: slide.image.thumb,
      })
    }
  })

  return (
    <Modal
      className="c-fullscreen-modal__content fullscreen-slider-modal"
      overlayClassName="c-fullscreen-modal__overlay"
      onRequestClose={onRequestClose}
      isOpen={isOpen}
    >
      <FullscreenContainer>
        <CloseButton onClick={onRequestClose}>
          <CrossOutIcon />
        </CloseButton>
        <FullscreenWrapper className="js-fullscreen-slider">
          <MainViewport ref={mainRef}>
            <MainContainer>{allSlides}</MainContainer>
            <div>
              <NavButton onClick={handlePrev} left>
                <ArrowLeft />
              </NavButton>
              <NavButton onClick={handleNext} right>
                <ArrowRight />
              </NavButton>
            </div>
          </MainViewport>
          <ThumbsViewport ref={thumbsRef}>
            <ThumbsContainer>
              {allThumbs.map((thumb, index) => (
                <ThumbSlide
                  key={thumb.key}
                  $selected={index === selectedIndex}
                  $backgroundImage={thumb.backgroundImage}
                  onClick={() => handleThumbClick(index)}
                  aria-label={`Go to slide ${index + 1}`}
                >
                  {thumb.isVideo && <VideoIcon />}
                </ThumbSlide>
              ))}
            </ThumbsContainer>
          </ThumbsViewport>
        </FullscreenWrapper>
      </FullscreenContainer>
    </Modal>
  )
}

export default FullscreenSlider
